import { ProjectMap } from "@/components/project/ProjectMap"
import { ProjectDataProvider } from "@/contexts/ProjectData"
import { StrictPosition } from "@fieldbook/common/types"
import { createFileRoute, Outlet } from "@tanstack/react-router"
import { SpeciesProfileSyncProvider } from "@/contexts/SpeciesProfileSync"
import { useNewTaskToasts } from "@/hooks/useNewTaskToasts"
import { OfflineMapTilesProvider } from "@/contexts/OfflineMapTiles"
import {
  ProjectMapVisibilityProvider,
  useProjectMapVisibility,
} from "@/contexts/ProjectMapVisibility"

import * as z from "zod"
import { useMemo } from "react"
import * as Sentry from "@sentry/react"
import { ErrorFallback } from "@/components/app/ErrorFallback"
import { SearchModeProvider } from "@/contexts/SearchMode"
import { UploadCompletionToasts } from "@/components/project/UploadStatus"

const searchSchema = z.object({
  focusOn: z.string().optional(),
})

function parseFocusOn(value: string | undefined): StrictPosition | undefined {
  if (!value) return undefined
  const [lng, lat] = value.split(",").map(Number)
  if (Number.isFinite(lng) && Number.isFinite(lat))
    return [lng, lat] as StrictPosition
  return undefined
}

export const Route = createFileRoute("/_private/projects/$id")({
  component: RouteComponent,
  validateSearch: (search) => searchSchema.parse(search),
  errorComponent: (error) => {
    Sentry.captureException(error)
    return <ErrorFallback error={error} resetError={() => location.reload()} />
  },
})

function RouteComponent() {
  const { id } = Route.useParams()
  const { focusOn: focusOnParam } = Route.useSearch()
  const focusOn = useMemo(() => parseFocusOn(focusOnParam), [focusOnParam])
  return (
    <ProjectDataProvider projectId={id}>
      <OfflineMapTilesProvider projectId={id}>
        <SpeciesProfileSyncProvider>
          <SearchModeProvider key={id}>
            <ProjectMapVisibilityProvider>
              <ProjectTaskToasts projectId={id} />
              <div className="relative h-full">
                {/* Mounted at this level so it survives navigation between the
                  project's sub-routes — except under a full-screen overlay
                  that has called useSuppressProjectMap. */}
                <SuppressibleProjectMap focusOn={focusOn} />

                {/* Announces finished large uploads on every sub-route. The
                  status itself is in the data sheet's header. */}
                <UploadCompletionToasts projectId={id} />

                {/* Child routes render on top */}
                <Outlet />
              </div>
            </ProjectMapVisibilityProvider>
          </SearchModeProvider>
        </SpeciesProfileSyncProvider>
      </OfflineMapTilesProvider>
    </ProjectDataProvider>
  )
}

const SuppressibleProjectMap = ({
  focusOn,
}: {
  focusOn: StrictPosition | undefined
}) => {
  const { isMapVisible } = useProjectMapVisibility()
  if (!isMapVisible) return null
  return <ProjectMap focusOn={focusOn} />
}

// Mounted as a sibling of the project view so the assignment toasts
// fire regardless of which sub-route the user is on.
const ProjectTaskToasts = ({ projectId }: { projectId: string }) => {
  useNewTaskToasts(projectId)
  return null
}
